import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { EBundlesViewModel } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import useFeatureFlags from 'api/useFeatureFlags';
import useOffers from 'api/useOffers';
import usePermissions from 'api/usePermissions';
import usePricing from 'api/usePricing';
import { useProduct } from 'api/useProduct';
import useSections from 'api/useSections';
import useTabs from 'api/useTabs';
import useTheme from 'api/useTheme';
import {
  EBadgeType,
  Offer,
  Section
} from 'common/contracts';
import {
  EFeatureFlag,
  EProductPriority,
  EPromotionsFormState,
  ESaleDiscountDisplayType,
  ESectionOfferType,
  ESpecialOfferInternalViewModel,
  OfferType
} from 'constants/enums';
import Decimal from 'decimal.js';
import { Promotions } from 'design-system/AcSidebar/constants';
import { useTabTracking } from 'hooks/useTabTracking';
import { useUnsavedChanges } from 'hooks/useUnsavedChanges';
import { convertProgressBarPointsToApiFormat } from 'utils/barPointsUtils';
import { isPriceLocalized as isPriceLocalizedUtil } from 'utils/isPriceLocalized';
import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';
import { OfferScheduleSection } from 'views/Offers/components/Schedule';
import {
  badgeFieldsSchema,
  generalFieldsSchema,
  productsFieldsSchema,
  saleDiscountFieldsSchema,
  segmentsFieldsSchema,
  standardPriceRefinement,
  tagsFieldsSchema} from 'views/Offers/components/sections';
import { useBadgeForm } from 'views/Offers/hooks/useBadgeForm';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { useOfferFormData } from 'views/Offers/hooks/useOfferFormData';
import { useOfferFormSubmit } from 'views/Offers/hooks/useOfferFormSubmit';
import { useProgressBarForm } from 'views/Offers/hooks/useProgressBarForm';
import {
  DEFAULT_OFFER_DESIGN_EXTERNAL_ID,
  EMPTY_TAB,
  PROMOTION_MODEL_BADGE_MAP
} from 'views/Offers/Offer.consts';
import { IProductProps, ProductProps } from 'views/Offers/Offer.types';
import {
  buildBaseOfferPayload,
  buildFormTitle,
  buildOfferBadges,
  buildProductsListForPreview,
  buildSchedulePayload,
  buildSectionTabPayload,
  checkUniqueness,
  createBadgeFilter,
  getInitialBadgeValues,
  getRelevantBadgeType,
  mapApiProductsToLocal
} from 'views/Offers/Offer.utils';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';
import { z } from 'zod';

import FormSectionsTabs from 'components/FormSectionsTabs';
import { Product } from 'components/ProductsForm/ProductsForm.types';
import { TabsItem } from 'components/Theme/StoreTheme/storeTheme.types';
import { Form } from 'components/ui';
import { Tabs, TabsContent } from 'components/ui/tabs';

import SettingsTab from './components/Tabs/SettingsTab';
import { useProductManagement } from './hooks/useProductManagement';
import { PromotionPreview } from './PromotionPreview';
import {
  MAP_OFFER_MODEL_TO_HAS_ONE_MAIN,
  PROMOTIONS_TABS
} from './PromotionsForm.consts';
import { buildPromotionFormInitialValues } from './PromotionsForm.utils';

import 'style/forms.scss';

const PROMOTIONS_SCHEMA = standardPriceRefinement(
  generalFieldsSchema
    .merge(saleDiscountFieldsSchema)
    .merge(productsFieldsSchema)
    .merge(tagsFieldsSchema)
    .merge(segmentsFieldsSchema)
    .merge(badgeFieldsSchema)
    .extend({
      setAsFree: z.boolean().default(false),
      offerId: z.string().optional(),
      type: z.string().optional().default(OfferType.SPECIAL_OFFER),
      quantity: z.string().optional().default(''),
      backgroundImage: z.string().optional().default(''),
      productsSequence: z.any().optional(),
      coolDownInHours: z.number().optional().default(0),
      playerAvailability: z
        .number()
        .min(1, 'Availability must be at least 1')
        .max(99, 'Availability cannot exceed 99')
        .optional(),
      schedule: z
        .object({
          permanent: z.boolean().optional(),
          timeFrames: z.array(z.any()).optional(),
          interval: z.string().optional()
        })
        .optional(),
      startHour: z
        .string()
        .optional()
        .refine(
          (val) => !val || /^([01]\d|2[0-3]):([0-5]\d)$/.test(val),
          { message: 'Invalid time format (HH:MM)' }
        ),
      active: z.boolean().default(true),
      progressBarId: z.string().nullable().optional(),
      progressBarPoints: z.number().nullable().optional()
    })
).superRefine((data, ctx) => {
  const needsAvailability =
    data.setAsFree || data.schedule?.interval || data.startHour;
  if (needsAvailability && !data.playerAvailability) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'Player availability is required for free or interval offers',
      path: ['playerAvailability']
    });
  }
});

type PromotionsFormFields = z.infer<typeof PROMOTIONS_SCHEMA>;

interface IPromotionsFormProps {
  edit?: boolean;
  dup?: boolean;
}

const PromotionsForm: React.FC<IPromotionsFormProps> = ({
  edit = false,
  dup = false
}): React.ReactElement => {
  const { publisherId, specialOfferId } = useParams();
  const navigate = useNavigate();
  const { canUserEdit } = usePermissions();

  const { updateOffer, addOffer, getOffer, getOffers, formatProductQuantity } =
    useOffers({
      offerId: specialOfferId,
      offerType: OfferType.SPECIAL_OFFER
    });
  const { submitOffer } = useOfferFormSubmit({
    edit,
    dup,
    offerLabel: 'Promotion',
    addOffer,
    updateOffer
  });

  const [tab, setTab] = useState(EPromotionsFormState.GENERAL);
  const [products, setProducts] = useState<ProductProps[]>([]);
  const [dirtyProducts, setDirtyProducts] = useState(false);
  const [startHour, setStartHour] = useState<string | undefined>('');
  const [intervalCron, setIntervalCron] = useState<string>('');

  const {
    progressBarFormValues,
    setProgressBarFormValues,
    isProgressBarFormValid,
    dirtyProgressBar,
    handleProgressBarFormChange
  } = useProgressBarForm();

  const {
    offerDesignOptions,
    defaultOfferDesignId,
    getOfferUI,
    setCurrentOfferUI
  } = useOfferDesignOptions({
    publisherId: publisherId || '',
    offerDesignType: OfferDesignTypes.SpecialOffer,
    defaultExternalId:
      DEFAULT_OFFER_DESIGN_EXTERNAL_ID[OfferType.SPECIAL_OFFER],
    offerId: specialOfferId
  });

  const handleDataLoaded = useCallback(
    (result: Partial<Offer>): void => {
      setCurrentOfferUI(result.offerUi?.offerUiId || '');
      setProducts(
        mapApiProductsToLocal(
          (result.productsSequence?.[0]?.products as any[]) || []
        )
      );
      setProgressBarFormValues({
        progressBarPoints:
          result.productsSequence?.[0]?.progressBarPoints?.[0]?.points || null,
        progressBarId:
          result.productsSequence?.[0]?.progressBarPoints?.[0]?.barId || null
      });
    },
    [setCurrentOfferUI, setProgressBarFormValues]
  );

  const { data, isLoading, chosenSegment, setChosenSegment } =
    useOfferFormData({
      offerId: specialOfferId,
      publisherId,
      getOffer,
      onDataLoaded: handleDataLoaded
    });

  const { getGeneralTheme: generalTheme } = useTheme(publisherId);
  const viewModel = generalTheme.data?.bundlesViewModel as EBundlesViewModel;
  const { featureFlags } = useFeatureFlags({ publisherId });

  const hasFeatureFlagTags = !!featureFlags?.[EFeatureFlag.DASHBOARD_TAGS];
  const hasFeatureFlagSetAsFree =
    !!featureFlags?.[EFeatureFlag.DASHBOARD_OFFERS_FREE];
  const hasScheduleOfferFeatureFlag =
    !!featureFlags?.[EFeatureFlag.DASHBOARD_SCHEDULED_OFFERS];
  const enableTabs = useMemo(
    (): boolean => !!featureFlags?.[EFeatureFlag.STORE_TABS],
    [featureFlags]
  );

  const { getSectionsByOfferType: getSections } = useSections(
    publisherId,
    ESectionOfferType.PROMOTION
  );
  const { getTabs } = useTabs(publisherId);
  const { getPricings } = usePricing();
  const { data: productsData, isLoading: isLoadingProducts } =
    useProduct(publisherId).getProducts;
  const productsList: Product[] = productsData?.result;

  const priceTypingDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isPriceLocalized, setIsPriceLocalized] = useState(true);

  useEffect(() => {
    if (!getPricings.data && !getPricings.isFetching) {
      getPricings.refetch();
    }
  }, [getPricings.data, getPricings.isFetching]);

  const {
    currentProduct,
    setCurrentProduct,
    handleQuantityChange,
    handlePriorityChange,
    removeProduct,
    editProduct,
    addProduct
  } = useProductManagement({
    products,
    setProducts,
    setDirtyProducts,
    productsData,
    formatProductQuantity
  });

  const bundleModelFromTheme = useMemo(
    (): ESpecialOfferInternalViewModel | undefined =>
      generalTheme.data?.specialOffersInternalViewModel,
    [generalTheme.data?.specialOffersInternalViewModel]
  );

  const relevantBadgeType: EBadgeType | 'both' = useMemo(
    () =>
      getRelevantBadgeType(
        bundleModelFromTheme,
        PROMOTION_MODEL_BADGE_MAP,
        viewModel
      ),
    [viewModel, bundleModelFromTheme]
  );

  const initialBadgeValues = useMemo(
    () =>
      getInitialBadgeValues(
        data?.badges,
        createBadgeFilter(relevantBadgeType, viewModel)
      ),
    [data?.badges, relevantBadgeType, viewModel]
  );

  const {
    badgeList,
    setSelectedBadgeId,
    selectedPublisherBadgeId
  } = useBadgeForm({
    publisherId,
    relevantBadgeType,
    viewModel,
    initialSelectedBadgeId: initialBadgeValues.badgeId
  });

  const initialValues = useMemo(
    () => ({
      ...buildPromotionFormInitialValues({
        data,
        edit,
        dup,
        defaultOfferDesignId,
        getOfferResult: getOffer?.data?.result
      }),
      badge: initialBadgeValues.badgeId,
      publisherBadgeId: initialBadgeValues.publisherBadgeId || ''
    }),
    [
      data,
      edit,
      dup,
      defaultOfferDesignId,
      getOffer?.data?.result,
      initialBadgeValues
    ]
  );

  const form = useForm<PromotionsFormFields>({
    resolver: zodResolver(PROMOTIONS_SCHEMA),
    mode: 'onChange',
    defaultValues: initialValues
  });

  const {
    watch,
    handleSubmit,
    formState: { isDirty, isValid },
    reset
  } = form;

  const values = watch();

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const watchedBadge = useWatch({ control: form.control, name: 'badge' });
  useEffect(() => {
    setSelectedBadgeId(watchedBadge ?? null);
  }, [watchedBadge, setSelectedBadgeId]);

  const handleCustomNavigate = useCallback(
    (value: string): void => {
      setTab(value as EPromotionsFormState);
    },
    []
  );

  const { handleTabChange } = useTabTracking({
    sidebarTab: Promotions.title,
    tabs: PROMOTIONS_TABS,
    customNavigate: handleCustomNavigate
  });

  useUnsavedChanges({ dirty: isDirty });

  const priceLocalizedCheckDebounce = useCallback(
    (price: number): void => {
      if (priceTypingDebounceTimeout.current) {
        clearTimeout(priceTypingDebounceTimeout.current);
      }
      priceTypingDebounceTimeout.current = setTimeout(() => {
        setIsPriceLocalized(
          isPriceLocalizedUtil(price, getPricings.data?.result)
        );
      }, 1000);
    },
    [getPricings.data?.result]
  );

  useEffect(() => {
    if (getPricings.data && values.price) {
      priceLocalizedCheckDebounce(
        new Decimal(values.price).times(100).toNumber()
      );
    }
  }, [getPricings.data, values.price, priceLocalizedCheckDebounce]);

  const sectionItems = useMemo(() => {
    const items = [{ value: '0', label: 'None' }];
    if (getSections?.data?.result?.length > 0) {
      const parsed = getSections.data.result.map((section: Section) => ({
        value: section.sectionId,
        label: section.name
      }));
      return items.concat(parsed);
    }
    return items;
  }, [getSections?.data?.result]);

  const tabItems = useMemo((): Array<{ value: string; label: string }> => {
    if (!enableTabs) return [];
    const base = [{ value: EMPTY_TAB.value, label: EMPTY_TAB.content }];
    if (getTabs.data?.length > 0) {
      const parsed = getTabs.data.map((tab: TabsItem) => ({
        value: tab.tabId,
        label: tab.name
      }));
      return base.concat(parsed);
    }
    return base;
  }, [enableTabs, getTabs.data]);

  const getProductsOptions = useMemo(() => {
    if (isLoadingProducts || !productsList) return [];
    const productIds = new Set(
      products.map((item) => item.productId)
    );
    const filtered = productsList.filter(
      (product: Product) => !productIds.has(product.productId as string)
    );
    if (filtered.length === 0) return [];
    return filtered.map((product) => ({
      url: product.images?.[0]?.url || '',
      name: product.name,
      key: product.name,
      value: product.productId || ''
    }));
  }, [productsList, products, isLoadingProducts]);

  const selectedProduct = useMemo(
    () =>
      getProductsOptions.find(
        (product) => product.value === currentProduct?.productId
      ) || null,
    [getProductsOptions, currentProduct?.productId]
  );

  const badgeListOptions = useMemo(
    () =>
      badgeList?.map((item: { value: string; content: string }) => ({
        value: item.value,
        label: item.content
      })) || [],
    [badgeList]
  );

  const isNameTaken = useMemo(
    (): boolean =>
      Boolean(
        getOffers.data?.offers?.find(
          (o: Offer) => o.name === values.name
        ) && !edit
      ),
    [getOffers.data?.offers, values.name, edit]
  );

  const isPublisherOfferIdTaken = useMemo(
    (): boolean =>
      !checkUniqueness(
        values?.publisherOfferId,
        'publisherOfferId',
        getOffers.data?.offers,
        data,
        edit && !dup
      ),
    [values?.publisherOfferId, getOffers.data?.offers, data, edit, dup]
  );

  const onSubmit = useCallback(
    (formValues: PromotionsFormFields): void => {
      const progressBarPoints = convertProgressBarPointsToApiFormat(
        progressBarFormValues
      );
      const offerBadges = buildOfferBadges(
        formValues.badge ?? null,
        formValues.publisherBadgeId ?? '',
        selectedPublisherBadgeId
      );
      const { sectionId, tabId } = buildSectionTabPayload(
        formValues.section,
        formValues.tab
      );
      const newOffer: Partial<Offer> & {
        sectionId?: string | null;
        tabId?: string | null;
      } = {
        ...buildBaseOfferPayload(
          formValues,
          chosenSegment,
          OfferType.SPECIAL_OFFER
        ),
        description: formValues.description || null,
        coolDownInHours: 1,
        schedule: buildSchedulePayload({
          permanent: formValues.schedule?.permanent ?? true,
          timeFrames: formValues.schedule?.timeFrames ?? [],
          ...(formValues.schedule?.interval && { interval: formValues.schedule.interval })
        }),
        badges: offerBadges,
        productSale: {
          sale: formValues.salePercentage || 0,
          type:
            (formValues.salePercentageDisplayType as ESaleDiscountDisplayType) ||
            ESaleDiscountDisplayType.PERCENTAGE
        },
        priceDiscount: {
          priceBeforeDiscount: formValues.priceBeforeDiscount
            ? formValues.priceBeforeDiscount * 100
            : null,
          discount: formValues.discount ?? 0,
          type:
            (formValues.priceDiscountDisplayType as ESaleDiscountDisplayType) ||
            ESaleDiscountDisplayType.PERCENTAGE
        },
        productsSequence: [
          {
            index: 1,
            progressBarPoints,
            products: products.map((p) => ({
              productId: p.productId,
              quantity: p.amount,
              ...(p.publisherProductId
                ? { publisherProductId: p.publisherProductId }
                : {}),
              priority: p.priority
            })),
            priceInUsdCents: formValues.setAsFree
              ? 0
              : new Decimal(formValues.price).mul(100).toNumber(),
            ...(formValues.playerAvailability
              ? { playerAvailability: formValues.playerAvailability }
              : undefined)
          }
        ],
        sectionId,
        tabId
      };

      submitOffer(newOffer, specialOfferId);
    },
    [
      progressBarFormValues,
      selectedPublisherBadgeId,
      chosenSegment,
      products,
      specialOfferId,
      submitOffer
    ]
  );

  const offersViewModel: ESpecialOfferInternalViewModel = useMemo(
    () =>
      generalTheme.data?.specialOffersInternalViewModel ||
      ESpecialOfferInternalViewModel.HUGE,
    [generalTheme.data]
  );

  const productsListForPreview = useMemo(() => {
    const list = buildProductsListForPreview(products as IProductProps[]);
    const hasOneMain = MAP_OFFER_MODEL_TO_HAS_ONE_MAIN[offersViewModel];

    if (hasOneMain && list.length > 0) {
      const isMainProductExist = list.some(
        (p) => p.priority === EProductPriority.MAIN
      );
      if (!isMainProductExist) {
        list[0].priority = EProductPriority.MAIN;
      }
    }

    return list;
  }, [products, offersViewModel]);

  const formTitle = useMemo(
    (): string =>
      buildFormTitle(edit, 'Promotion', getOffer.data?.result?.name),
    [edit, getOffer.data?.result?.name]
  );

  const hasUnsavedChanges = useMemo(
    (): boolean => !edit || isDirty || dirtyProducts || dirtyProgressBar,
    [edit, isDirty, dirtyProducts, dirtyProgressBar]
  );

  const isPermanentIntervalMissingStartHour =
    values?.schedule?.permanent &&
    Boolean(values?.schedule?.interval) &&
    !values?.startHour;

  const isScheduledOfferMissingTimeFrames =
    hasScheduleOfferFeatureFlag &&
    !values?.schedule?.permanent &&
    !values?.schedule?.timeFrames?.length;

  const isFormValid = useMemo(
    (): boolean =>
      !isLoading &&
      isValid &&
      isProgressBarFormValid &&
      hasUnsavedChanges &&
      canUserEdit &&
      !isPublisherOfferIdTaken &&
      !isPermanentIntervalMissingStartHour &&
      !isScheduledOfferMissingTimeFrames,
    [
      isLoading,
      isValid,
      isProgressBarFormValid,
      hasUnsavedChanges,
      canUserEdit,
      isPublisherOfferIdTaken,
      isPermanentIntervalMissingStartHour,
      isScheduledOfferMissingTimeFrames
    ]
  );

  const handleSave = useCallback((): void => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const handleClose = useCallback((): void => {
    navigate('../');
  }, [navigate]);

  const handleSegmentsChange = useCallback(
    (newSegments: string[]): void => {
      setChosenSegment(newSegments);
      form.setValue('segments', newSegments, { shouldDirty: true });
    },
    [setChosenSegment, form]
  );

  const handleEditProduct = useCallback(
    (product: IProductProps): void => {
      editProduct(product.productId);
    },
    [editProduct]
  );

  const handleDeleteProduct = useCallback(
    (product: IProductProps): void => {
      removeProduct(product.productId);
    },
    [removeProduct]
  );

  const handleReorderProducts = useCallback(
    (reordered: IProductProps[]): void => {
      setDirtyProducts(true);
      setProducts(reordered as ProductProps[]);
    },
    []
  );

  const mappedCurrentProduct = useMemo(() => {
    if (!currentProduct) return null;
    return {
      productId: currentProduct.productId || '',
      quantity: currentProduct.quantity || 0,
      priority: currentProduct.priority?.toString() || EProductPriority.SUB,
      publisherProductId: currentProduct.publisherProductId
    };
  }, [currentProduct]);

  const handleCurrentProductChange = useCallback(
    (product: { productId: string; quantity: number | string; priority: string; publisherProductId?: string } | null): void => {
      if (product) {
        setCurrentProduct({
          productId: product.productId,
          quantity: product.quantity,
          publisherProductId: product.publisherProductId,
          priority: product.priority
        });
      } else {
        setCurrentProduct(null);
      }
    },
    []
  );

  const isOfferTabDisabled = getOffer.isLoading && edit;

  const formSectionTabs = useMemo(() => {
    const tabs = [
      {
        value: EPromotionsFormState.GENERAL,
        label: 'Settings',
        disabled: isOfferTabDisabled
      }
    ];
    if (hasScheduleOfferFeatureFlag) {
      tabs.push({
        value: EPromotionsFormState.SCHEDULE,
        label: 'Schedule',
        disabled: isOfferTabDisabled
      });
    }
    return tabs;
  }, [isOfferTabDisabled, hasScheduleOfferFeatureFlag]);

  return (
    <OfferFormLayout
      title={formTitle}
      isFormValid={isFormValid}
      onSave={handleSave}
      onClose={handleClose}
      previewContent={
        <PromotionPreview
          getOfferUI={getOfferUI}
          productsListForPreview={productsListForPreview}
          price={values.price}
          setAsFree={values.setAsFree}
          salePercentage={values.salePercentage}
          playerAvailability={values.playerAvailability}
        />
      }
    >
      <Form {...form}>
        <Tabs
          className="h-full pt-10 pb-10 w-[528px]"
          value={tab}
          onValueChange={handleTabChange}
        >
          <FormSectionsTabs
            currentTab={tab}
            tabs={formSectionTabs}
          />
          <div className="flex-1 flex justify-center">
            <TabsContent
              value={EPromotionsFormState.GENERAL}
              className="mt-9 w-full"
            >
              <SettingsTab
                offerDesignOptions={offerDesignOptions}
                onOfferDesignChange={setCurrentOfferUI}
                chosenSegment={chosenSegment}
                onSegmentsChange={handleSegmentsChange}
                hasFeatureFlagTags={hasFeatureFlagTags}
                hasFeatureFlagSetAsFree={hasFeatureFlagSetAsFree}
                isNameTaken={isNameTaken}
                isPriceLocalized={isPriceLocalized}
                sectionItems={sectionItems}
                tabItems={tabItems}
                enableTabs={!!enableTabs}
                relevantBadgeType={relevantBadgeType}
                badgeList={badgeListOptions}
                products={products}
                productOptions={getProductsOptions}
                currentProduct={mappedCurrentProduct}
                selectedProduct={selectedProduct}
                onCurrentProductChange={handleCurrentProductChange}
                onQuantityChange={handleQuantityChange}
                onPriorityChange={handlePriorityChange}
                onAddProduct={addProduct}
                onEditProduct={handleEditProduct}
                onDeleteProduct={handleDeleteProduct}
                onReorderProducts={handleReorderProducts}
                progressBarFormValues={progressBarFormValues}
                handleProgressBarFormChange={handleProgressBarFormChange}
                onStartHourChange={setStartHour}
              />
            </TabsContent>
            {hasScheduleOfferFeatureFlag && (
              <TabsContent
                value={EPromotionsFormState.SCHEDULE}
                className="mt-9 w-full"
              >
                <OfferScheduleSection
                  startHour={startHour ?? ''}
                  setStartHour={setStartHour}
                  intervalCron={intervalCron}
                  setIntervalCron={setIntervalCron}
                  showScheduleAvailability={
                    values.type === OfferType.SPECIAL_OFFER
                  }
                />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </Form>
    </OfferFormLayout>
  );
};

export default PromotionsForm;
